import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  bulkUpdateStatusActive,
  createStatus,
  deleteStatus,
  getStatusDetail,
  getStatusesWithFilters,
  getStatusStatistics,
  reorderStatuses,
  toggleStatusActive,
  updateStatus,
  validateStatus,
  validateStatusTransition,
  validateStatusUpdate,
} from "../services/orderStatusService";
import {
  BulkUpdateOrderStatusRequest,
  CreateOrderStatusRequest,
  OrderStatus,
  OrderStatusFilters,
  PaginatedOrderStatusResponse,
  ReorderStatusesRequest,
  UpdateOrderStatusRequest,
} from "../types/admin";

// Query keys
export const orderStatusKeys = {
  all: ["orderStatuses"] as const,
  lists: () => [...orderStatusKeys.all, "list"] as const,
  list: (filters: OrderStatusFilters) =>
    [...orderStatusKeys.lists(), filters] as const,
  details: () => [...orderStatusKeys.all, "detail"] as const,
  detail: (id: number) => [...orderStatusKeys.details(), id] as const,
  statistics: () => [...orderStatusKeys.all, "statistics"] as const,
};

// Hooks for fetching data
export const useOrderStatuses = (filters: OrderStatusFilters) => {
  return useQuery({
    queryKey: orderStatusKeys.list(filters),
    queryFn: () => getStatusesWithFilters(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useOrderStatusDetail = (id: number, enabled = true) => {
  return useQuery({
    queryKey: orderStatusKeys.detail(id),
    queryFn: () => getStatusDetail(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useOrderStatusStatistics = () => {
  return useQuery({
    queryKey: orderStatusKeys.statistics(),
    queryFn: () => getStatusStatistics(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hooks for mutations
export const useOrderStatusMutations = () => {
  const queryClient = useQueryClient();

  const createStatusMutation = useMutation({
    mutationFn: (request: CreateOrderStatusRequest) => createStatus(request),
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: UpdateOrderStatusRequest;
    }) => updateStatus(id, request),
    onSuccess: (data, variables) => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
      // Update the specific status detail cache
      queryClient.setQueryData(orderStatusKeys.detail(variables.id), data);
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: (id: number) => deleteStatus(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
      // Remove the specific status detail from cache
      queryClient.removeQueries({ queryKey: orderStatusKeys.detail(id) });
    },
  });

  const toggleStatusActiveMutation = useMutation({
    mutationFn: (id: number) => toggleStatusActive(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
      // Update the specific status detail cache
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.detail(id) });
    },
  });

  const bulkUpdateStatusActiveMutation = useMutation({
    mutationFn: (request: BulkUpdateOrderStatusRequest) =>
      bulkUpdateStatusActive(request),
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
    },
  });

  const reorderStatusesMutation = useMutation({
    mutationFn: (request: ReorderStatusesRequest) => reorderStatuses(request),
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
    },
  });

  return {
    createStatus: createStatusMutation,
    updateStatus: updateStatusMutation,
    deleteStatus: deleteStatusMutation,
    toggleStatusActive: toggleStatusActiveMutation,
    bulkUpdateStatusActive: bulkUpdateStatusActiveMutation,
    reorderStatuses: reorderStatusesMutation,
  };
};

// Hooks for validation
export const useOrderStatusValidation = () => {
  const validateStatusMutation = useMutation({
    mutationFn: (request: CreateOrderStatusRequest) => validateStatus(request),
  });

  const validateStatusUpdateMutation = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: UpdateOrderStatusRequest;
    }) => validateStatusUpdate(id, request),
  });

  const validateStatusTransitionMutation = useMutation({
    mutationFn: ({
      fromStatusId,
      toStatusId,
    }: {
      fromStatusId: number;
      toStatusId: number;
    }) => validateStatusTransition(fromStatusId, toStatusId),
  });

  return {
    validateStatus: validateStatusMutation,
    validateStatusUpdate: validateStatusUpdateMutation,
    validateStatusTransition: validateStatusTransitionMutation,
  };
};

// Hook for optimistic updates
export const useOptimisticOrderStatusUpdate = () => {
  const queryClient = useQueryClient();

  const updateOrderStatusOptimistically = (
    statusId: number,
    updates: Partial<OrderStatus>,
  ) => {
    // Update the specific status detail cache
    queryClient.setQueryData(
      orderStatusKeys.detail(statusId),
      (old: OrderStatus | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      },
    );

    // Update the status in all lists
    queryClient.setQueriesData(
      { queryKey: orderStatusKeys.lists() },
      (old: PaginatedOrderStatusResponse | undefined) => {
        if (!old || !old.statuses) return old;
        return {
          ...old,
          statuses: old.statuses.map((status) =>
            status.id === statusId ? { ...status, ...updates } : status,
          ),
        };
      },
    );
  };

  return { updateOrderStatusOptimistically };
};

// Hook for bulk operations
export const useOrderStatusBulkOperations = () => {
  const queryClient = useQueryClient();

  const bulkToggleActiveMutation = useMutation({
    mutationFn: (request: BulkUpdateOrderStatusRequest) =>
      bulkUpdateStatusActive(request),
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.statistics() });
    },
  });

  const reorderStatusesMutation = useMutation({
    mutationFn: (request: ReorderStatusesRequest) => reorderStatuses(request),
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusKeys.lists() });
    },
  });

  return {
    bulkToggleActive: bulkToggleActiveMutation,
    reorderStatuses: reorderStatusesMutation,
  };
};
